/**
 * Native Stripe wallet seam (T35.7.2.2 — native checkout) — the OS Google/Apple
 * Pay sheet for the installed Capacitor app, behind the app's adapter seam.
 *
 * Why this exists. Stripe's web `<PaymentElement>` wallet button rides the
 * browser **Payment Request API**, which is unreliable inside the Android System
 * WebView the native shell runs (it works in mobile Chrome / PWA but not the
 * installed app). On native we therefore drive the OS wallet sheet via the native
 * Stripe SDK through `@capacitor-community/stripe`. The web Payment Element stays
 * the web/PWA surface AND the native fallback (this seam no-ops off native, and
 * the keyless tier falls through to it), so the branch never regresses web.
 *
 * Same money path as the web Payment Element. The sheet is presented against the
 * **same `createSession` client secret** (`descriptor.primary.sessionToken`) — a
 * one-time PaymentIntent or a recurring first-invoice `pi_…` secret — and the row
 * finalizes from the **same Stripe webhook** (`.1`'s intake →
 * `resolveSubscriptionStatus`). This module only presents the sheet and reports
 * the collection outcome up; it never writes `subscriptions` and never marks the
 * row `active`. A `Completed` result means charged-at-Stripe-but-still-pending —
 * identical to the web path's `onResult(true)` (the success UI reads PENDING).
 *
 * PCI SAQ-A. The native SDK tokenizes the card in the OS wallet sheet — no card
 * data reaches our DOM or our servers.
 *
 * Portability (CP1–CP4). This file lives in `apps/web` precisely because the
 * `@capacitor-community/stripe` plugin import is allowed here, never in
 * `packages/shared`. The plugin is reached ONLY on native and ONLY via a dynamic
 * `import()`, so the web bundle never eagerly loads it and no native type leaks
 * into shared. Mirrors the `appUpdate.ts` / `secureStorage.ts` seams.
 *
 * Render decision. The render branch swaps to the native sheet ONLY after the
 * async `probeStripeNativeReady()` proves the wallet can genuinely run on this
 * device (plugin compiled in + SDK init + Google/Apple Pay provisioned). Any
 * probe failure leaves the user on the web `<PaymentElement>` floor — the
 * synchronous `stripeNativeAvailability` (native + key) is NOT a real capability
 * check and must not gate the render alone.
 *
 * Testability. `initStripeNative()`, `probeStripeNativeReady()` and
 * `presentStripeNativeSheet()` take their platform probe and their plugin as
 * injectable options, so the no-op contract, the keyless gate, the capability
 * probe, the present-against-the-client-secret call, and the result-mapping are
 * all pinned by unit tests with no device and no real plugin.
 */

import { isNativePlatform } from '../routes/nativeGoogleSignIn';

/** Publishable key the native SDK initializes with (the web Stripe.js value). */
const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

/**
 * The three terminal results the native payment sheet returns: one of
 * `completed` | `canceled` | `failed`. Mirrors the plugin's terminal sheet
 * values without importing the native value set into this seam's public surface
 * (so callers stay plugin-free).
 */
export type StripeNativeSheetResult = 'completed' | 'canceled' | 'failed';

/**
 * The minimal slice of the `@capacitor-community/stripe` surface this seam uses:
 * `initialize` (once, with the publishable key), then — per checkout — the
 * dedicated WALLET LAUNCHER pair for whichever wallet the device carries:
 * `createGooglePay`/`presentGooglePay` (Android) or `createApplePay`/
 * `presentApplePay` (iOS). Each `present*` resolves with the wallet's terminal
 * result string — one of `"googlePayCompleted" | "googlePayCanceled" |
 * "googlePayFailed"` for Google. Kept as an interface (not the plugin directly)
 * so a fake is injectable in tests.
 *
 * Why the launcher, not `createPaymentSheet`. `createPaymentSheet` presents the
 * multi-method Stripe PaymentSheet (Link / Card / Klarna), which SUPPRESSES the
 * Google Pay button inside the System WebView (`google_pay_enabled=false`,
 * `W/StripeSdk: Google Pay is not enabled for this session`). The dedicated
 * GooglePayLauncher (`createGooglePay`/`presentGooglePay`) launches the REAL OS
 * Google Pay sheet — the same surface the `isGooglePayAvailable()` probe checks,
 * so probe and present finally agree. The launcher reads the merchant name,
 * country code and testing mode from the Android manifest `<meta-data>` we inject
 * at build time, so `createGooglePay` needs only `{ paymentIntentClientSecret }`.
 */
export interface StripeNativePlugin {
  initialize(opts: { publishableKey: string }): Promise<void>;
  /** Prepare the OS Google Pay launcher against the client secret (Android). */
  createGooglePay(opts: { paymentIntentClientSecret: string }): Promise<void>;
  /** Present the OS Google Pay sheet; resolves with the terminal `googlePay*` result. */
  presentGooglePay(): Promise<{ paymentResult: string }>;
  /**
   * Prepare the OS Apple Pay launcher (iOS). Apple's launcher needs the line items,
   * merchant id, country and currency up front (unlike Google's, which reads them
   * from the Android manifest).
   */
  createApplePay(opts: {
    paymentIntentClientSecret: string;
    paymentSummaryItems: Array<{ label: string; amount: number }>;
    merchantIdentifier: string;
    countryCode: string;
    currency: string;
  }): Promise<void>;
  /** Present the OS Apple Pay sheet; resolves with the terminal `applePay*` result. */
  presentApplePay(): Promise<{ paymentResult: string }>;
  /**
   * Wallet capability probes. The plugin's `isGooglePayAvailable()` /
   * `isApplePayAvailable()` RESOLVE when the wallet is provisioned on the device
   * and REJECT (throw) when it is not — there is no boolean return. The probe
   * treats a resolve as "available" and a rejection as "unavailable".
   */
  isGooglePayAvailable(): Promise<void>;
  isApplePayAvailable(): Promise<void>;
}

/** Optional diagnostics — surfaces an init / present failure (redacted at the call site). */
export interface StripeNativeDiagnostics {
  onError?: (step: string, error: unknown) => void;
}

export interface InitStripeNativeOptions {
  /** Platform probe. Defaults to `isNativePlatform()`. Tests inject `() => false` / `() => true`. */
  isNative?: () => boolean;
  /** Plugin override. Defaults to the dynamically-imported native plugin. Tests inject a fake. */
  plugin?: StripeNativePlugin;
  /** Publishable key. Defaults to `VITE_STRIPE_PUBLISHABLE_KEY`. */
  publishableKey?: string | undefined;
  diagnostics?: StripeNativeDiagnostics;
}

/** Whether the native wallet sheet is the active Stripe surface on this platform. */
export interface StripeNativeAvailability {
  /** True only inside the native shell AND with a publishable key configured. */
  native: boolean;
}

/**
 * Decide whether the native Stripe sheet should drive checkout here. Native shell
 * + a configured publishable key → native sheet; otherwise (web/PWA, or a keyless
 * native tier) → the caller falls back to the web Payment Element. Pure +
 * synchronous so the render branch can read it without awaiting.
 */
export function stripeNativeAvailability(
  options: Pick<InitStripeNativeOptions, 'isNative' | 'publishableKey'> = {},
): StripeNativeAvailability {
  const isNative = options.isNative ?? isNativePlatform;
  const publishableKey = resolvePublishableKey(options);
  // Keyless native tier mirrors StripePaymentElement's placeholder gate: no key
  // → no native SDK → fall through to the web Payment Element fallback.
  return { native: isNative() && Boolean(publishableKey) };
}

/**
 * The publishable key in effect: a key PASSED in options wins (even an explicit
 * `undefined`, so a test can drive the keyless tier), otherwise the baked
 * `VITE_STRIPE_PUBLISHABLE_KEY`. Using `'in'` (not `??`) keeps an explicit
 * `undefined` from falling back to the env key.
 */
function resolvePublishableKey(
  options: Pick<InitStripeNativeOptions, 'publishableKey'>,
): string | undefined {
  return 'publishableKey' in options ? options.publishableKey : PUBLISHABLE_KEY;
}

let initialized = false;

/**
 * Initialize the native Stripe SDK with the publishable key — once per app
 * session. A NO-OP off native or on a keyless tier (returns without importing or
 * touching the plugin), so the call site stays platform-free and the web bundle
 * never eagerly loads the native plugin. Run it twice and it does the same thing:
 * a second call after a successful init returns immediately.
 *
 * @returns true if the native SDK is initialized and ready; false when this is a
 *          web / keyless tier (the caller uses the web Payment Element fallback).
 */
export async function initStripeNative(options: InitStripeNativeOptions = {}): Promise<boolean> {
  const { native } = stripeNativeAvailability(options);
  if (!native) return false;
  if (initialized) return true;

  const publishableKey = resolvePublishableKey(options);
  // `native` is already false without a key, so this is non-null here.
  if (!publishableKey) return false;

  try {
    const plugin = options.plugin ?? (await loadStripeNativePlugin());
    await plugin.initialize({ publishableKey });
    initialized = true;
    return true;
  } catch (error) {
    options.diagnostics?.onError?.('initialize', error);
    return false;
  }
}

/**
 * Async capability probe — the REAL "can the native wallet sheet actually run
 * here?" check the render branch reads (not the synchronous
 * `stripeNativeAvailability`, which only knows native + key). Returns `true` ONLY
 * when ALL hold:
 *   - `isNative()` is true (the installed Capacitor shell, not web / PWA);
 *   - a publishable key is configured;
 *   - the plugin loads AND the native SDK initializes (`initStripeNative`);
 *   - Google Pay OR Apple Pay is genuinely provisioned on this device — the
 *     plugin's `isGooglePayAvailable()` / `isApplePayAvailable()` RESOLVE (they
 *     reject/throw when the wallet is missing, the plugin isn't in the APK, etc.).
 *
 * Fail-soft: ANY throw / rejection anywhere — plugin not compiled into the build,
 * SDK init failure, both wallet probes rejecting — returns `false`. It NEVER
 * throws, so the caller stays on the web `<PaymentElement>` floor. Injectable
 * (plugin + platform probe) exactly like the rest of the seam, so it unit-tests
 * with no device.
 */
export async function probeStripeNativeReady(
  options: InitStripeNativeOptions = {},
): Promise<boolean> {
  try {
    const ready = await initStripeNative(options);
    if (!ready) return false;

    const plugin = options.plugin ?? (await loadStripeNativePlugin());
    const googlePay = await probeWallet(() => plugin.isGooglePayAvailable());
    if (googlePay) return true;
    const applePay = await probeWallet(() => plugin.isApplePayAvailable());
    return applePay;
  } catch (error) {
    options.diagnostics?.onError?.('probe', error);
    return false;
  }
}

/**
 * Run one wallet-availability probe: the plugin RESOLVES when the wallet is
 * provisioned and REJECTS when it is not. Map resolve → true, reject → false; a
 * rejection here is an expected "wallet not available", not a probe failure.
 */
async function probeWallet(probe: () => Promise<void>): Promise<boolean> {
  try {
    await probe();
    return true;
  } catch {
    return false;
  }
}

export interface PresentStripeNativeSheetOptions extends InitStripeNativeOptions {
  /**
   * The session client secret to charge against — `descriptor.primary.sessionToken`
   * (a one-time PaymentIntent or the recurring first-invoice `pi_…` secret). The
   * SAME secret the web Payment Element confirms; the row finalizes from the SAME
   * webhook either way.
   */
  clientSecret: string;
  /** Shown on the OS wallet sheet (the backed idea / "Shopista"); receipts name it server-side. */
  merchantDisplayName?: string | undefined;
  /** ISO-3166-1 alpha-2 country for the wallet (Shopista is Canada). */
  countryCode?: string | undefined;
  /**
   * Drive the Google Pay sheet in test mode (Stripe test-key checkout). Defaults
   * to false; the caller passes true on the local / dev tiers.
   *
   * The Android GooglePayLauncher reads its testing mode from the manifest
   * `<meta-data>` injected at build time, so this no longer drives the Google
   * path; it is retained for the option's stable shape and possible iOS use.
   */
  googlePayIsTesting?: boolean | undefined;
  /**
   * iOS Apple Pay only — the line-item amount (in the currency's minor units) and
   * its label, plus the Apple merchant id and currency. The Android Google Pay
   * launcher ignores these (it reads everything from the manifest). Optional with
   * Canada-dollar defaults so the existing call sites stay unchanged on Android.
   */
  applePay?: {
    amount: number;
    label?: string | undefined;
    merchantIdentifier: string;
    currency?: string | undefined;
  };
}

/**
 * Present the REAL OS Google/Apple Pay sheet against the session client secret via
 * the dedicated wallet LAUNCHER (not the multi-method PaymentSheet) and map the
 * terminal result. Ensures the SDK is initialized first (so a single call does the
 * whole native checkout), then probes which wallet the device carries — Google
 * first, then Apple, REUSING the same `isGooglePayAvailable` / `isApplePayAvailable`
 * checks the render-gating probe uses, so present and probe agree — and launches
 * that wallet's `create*`/`present*` pair.
 *
 * A `completed` result is charged-at-Stripe-but-still-pending; the caller raises
 * its `onResult(true)` exactly as the web confirm does, and the webhook moves the
 * row to `active`. `canceled` is a user dismissal (no error UI); `failed` (or a
 * thrown plugin error, mapped to `failed` + logged) shows the retryable error.
 *
 * Off native / keyless this should never be called (the render branch picks the
 * web element instead); if it is, it returns `failed` without touching the plugin.
 * If neither wallet is available at present time (the probe passed earlier but the
 * device changed), it returns `failed` and logs — the caller still shows the card
 * form beneath.
 */
export async function presentStripeNativeSheet(
  options: PresentStripeNativeSheetOptions,
): Promise<StripeNativeSheetResult> {
  const ready = await initStripeNative(options);
  if (!ready) {
    options.diagnostics?.onError?.(
      'present',
      new Error('Native Stripe SDK not available (web / keyless tier).'),
    );
    return 'failed';
  }

  try {
    const plugin = options.plugin ?? (await loadStripeNativePlugin());

    // Google first (Android), then Apple (iOS) — mirrors probeStripeNativeReady so
    // the wallet we present is the one the render gate already proved available.
    if (await probeWallet(() => plugin.isGooglePayAvailable())) {
      await plugin.createGooglePay({ paymentIntentClientSecret: options.clientSecret });
      const { paymentResult } = await plugin.presentGooglePay();
      return mapGooglePayResult(paymentResult);
    }

    if (await probeWallet(() => plugin.isApplePayAvailable())) {
      const apple = options.applePay;
      await plugin.createApplePay({
        paymentIntentClientSecret: options.clientSecret,
        paymentSummaryItems: [
          {
            label: apple?.label ?? options.merchantDisplayName ?? 'Shopista',
            // Apple's launcher needs the line amount; default to 0 so an
            // Android-shaped call (no `applePay`) still type-checks — iOS callers
            // pass a real amount.
            amount: apple?.amount ?? 0,
          },
        ],
        merchantIdentifier: apple?.merchantIdentifier ?? '',
        countryCode: options.countryCode ?? 'CA',
        currency: apple?.currency ?? 'CAD',
      });
      const { paymentResult } = await plugin.presentApplePay();
      return mapApplePayResult(paymentResult);
    }

    options.diagnostics?.onError?.(
      'present',
      new Error('No wallet available at present time (Google/Apple Pay both rejected).'),
    );
    return 'failed';
  } catch (error) {
    options.diagnostics?.onError?.('present', error);
    return 'failed';
  }
}

/**
 * Map the Google Pay launcher's terminal result string to our neutral result.
 * `googlePayCompleted` → completed; `googlePayCanceled` → canceled; anything else
 * (incl. `googlePayFailed`) → failed.
 */
function mapGooglePayResult(paymentResult: string): StripeNativeSheetResult {
  if (paymentResult === 'googlePayCompleted') return 'completed';
  if (paymentResult === 'googlePayCanceled') return 'canceled';
  return 'failed';
}

/**
 * Map the Apple Pay launcher's terminal result string to our neutral result.
 * `applePayCompleted` → completed; `applePayCanceled` → canceled; anything else
 * (incl. `applePayFailed`) → failed.
 */
function mapApplePayResult(paymentResult: string): StripeNativeSheetResult {
  if (paymentResult === 'applePayCompleted') return 'completed';
  if (paymentResult === 'applePayCanceled') return 'canceled';
  return 'failed';
}

/**
 * Wrap the raw Capacitor plugin proxy in a plain, NON-THENABLE facade. The
 * `@capacitor/core` proxy forwards EVERY property access to the native bridge —
 * including `.then`. So if an `async` function RETURNS the proxy (or you `await`
 * it), the Promise machinery probes `.then` on the resolved value and invokes the
 * non-existent native `Stripe.then()`, throwing `"Stripe.then()" is not
 * implemented on android` (Sentry `shopista-front`, env dev). The facade exposes
 * ONLY this seam's interface methods and has no `.then`, so it is safe to
 * await/return. Each arrow references `raw` LAZILY (only inside the call), so
 * building the facade never touches `raw.then`. Mirrors the secureStorage.ts seam
 * (which calls the proxy's methods and never returns it). Exported for the
 * regression test.
 */
export function wrapStripeNativePlugin(raw: StripeNativePlugin): StripeNativePlugin {
  return {
    initialize: (opts) => raw.initialize(opts),
    createGooglePay: (opts) => raw.createGooglePay(opts),
    presentGooglePay: () => raw.presentGooglePay(),
    createApplePay: (opts) => raw.createApplePay(opts),
    presentApplePay: () => raw.presentApplePay(),
    isGooglePayAvailable: () => raw.isGooglePayAvailable(),
    isApplePayAvailable: () => raw.isApplePayAvailable(),
  };
}

/**
 * Native-only loader for the `@capacitor-community/stripe` plugin. Dynamically
 * imported so the web bundle never eagerly pulls native code (CP1); reached only
 * on the native branch above. This is the ONLY place the plugin module is
 * referenced. Returns the NON-THENABLE facade, never the raw proxy (see
 * `wrapStripeNativePlugin`).
 */
async function loadStripeNativePlugin(): Promise<StripeNativePlugin> {
  const { Stripe } = await import('@capacitor-community/stripe');
  return wrapStripeNativePlugin(Stripe as unknown as StripeNativePlugin);
}

/** Test-only reset of the once-per-session init latch. */
export function __resetStripeNativeInitForTests(): void {
  initialized = false;
}
